import { useState, useEffect } from 'react';
import {
    formatMillions
} from 'src/Utils';
import { formatNumber } from "src/MarketDataComponents/Helpers";

import moment from 'moment';

const N_A = 'n/a';
const FIELD_YEAR_END_FORMATTED = 'yearEndFormatted'; 

const RENDER_METHOD_NUMBER = 'number';
const RENDER_METHOD_STR = 'string';
const RENDER_METHOD_SHARE = 'share';
const RENDER_METHOD_HEADER = 'header';
const RENDER_METHOD_DECIMAL = 'decimal';

export default function IncomeStatement({incomeStatement}) {
    const [incomeStatementFormatted, setIncomeStatementFormatted] = useState(null);
    const [currency, setCurrency] = useState(null);

    const rowsToRender = [
        {
            title: 'Fiscal year ends', 
            fieldName: FIELD_YEAR_END_FORMATTED, 
            renderMethod: RENDER_METHOD_STR, 
            trClassNames: 'grey-0-bg font-weight-bold data-table-row-header'
        },
        {
            title: 'Turnover', 
            fieldName: 'revenue', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Expenses', 
            fieldName: 'expenses', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'EBITDA', 
            fieldName: 'EBITDA', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'EBIT', 
            fieldName: 'EBIT', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Operating profit (reported)', 
            fieldName: 'operatingProfitReported', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Operating profit (adjusted)', 
            fieldName: 'operatingProfitAdjusted',
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Investment income', 
            fieldName: 'investmentIncome', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Exceptional items', 
            fieldName: 'exceptionalItems', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Net interest', 
            fieldName: 'netInterest', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Pre-tax profit', 
            fieldName: 'incomePreTax', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Tax', 
            fieldName: 'tax', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Net profit', 
            fieldName: 'netProfit', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Minority interests', 
            fieldName: 'minorityInterests', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Profit for financial year', 
            fieldName: 'profitForFinancialYear', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Ordinary dividends', 
            fieldName: 'ordinaryDividends', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Retained profit', 
            fieldName: 'retainedProfit', 
            renderMethod: RENDER_METHOD_NUMBER, 
            trClassNames: ''
        },
        {
            title: 'Per share data', 
            fieldName: 'perShare', 
            renderMethod: RENDER_METHOD_HEADER, 
            trClassNames: 'grey-0-bg font-weight-bold data-table-row-header'
        },
        {
            title: 'DPS p', 
            fieldName: 'dividendPerShare', 
            renderMethod: RENDER_METHOD_SHARE, 
            trClassNames: ''
        },
        {
            title: 'Normalized EPS p', 
            fieldName: 'normalisedEarningsPerShare', 
            renderMethod: RENDER_METHOD_SHARE, 
            trClassNames: ''
        },
        {
            title: 'Reported EPS p', 
            fieldName: 'reportedEarningsPerShare', 
            renderMethod: RENDER_METHOD_SHARE, 
            trClassNames: ''
        },
        {
            title: 'Investment ratios', 
            fieldName: 'investmentRatios', 
            renderMethod: RENDER_METHOD_HEADER, 
            trClassNames: 'grey-0-bg font-weight-bold data-table-row-header'
        },
        {
            title: 'Operating margin', 
            fieldName: 'operatingMargin', 
            renderMethod: RENDER_METHOD_DECIMAL, 
            trClassNames: ''
        },
        {
            title: 'DPS growth %', 
            fieldName: 'dividendPerShareGrowth1Y', 
            renderMethod: RENDER_METHOD_DECIMAL, 
            trClassNames: ''
        },
        {
            title: 'Dividend cover x', 
            fieldName: 'dividendCover', 
            renderMethod: RENDER_METHOD_DECIMAL, 
            trClassNames: ''
        },
        {
            title: 'Norm EPS growth %', 
            fieldName: 'normalisedEarningsPerShareGrowth', 
            renderMethod: RENDER_METHOD_DECIMAL, 
            trClassNames: ''
        },
        {
            title: 'Reported EPS growth %', 
            fieldName: 'reportedEarningsPerShareGrowth', 
            renderMethod: RENDER_METHOD_DECIMAL, 
            trClassNames: ''
        }
    ];

    useEffect(() => {
        const incomeStatementArr = [...incomeStatement];
        incomeStatementArr.reverse();
        let incomeStatementCurrency = null;

        for (let c in incomeStatementArr) {
            try { 
                incomeStatementCurrency = incomeStatementArr[c].currency;
                incomeStatementArr[c][FIELD_YEAR_END_FORMATTED] = moment(incomeStatementArr[c].yearEnd, "YYYY-MM-DD").format("DD/MM/YYYY");
            } catch (e) {}
        }

        setCurrency(incomeStatementCurrency);
        setIncomeStatementFormatted(incomeStatementArr);
    }, []);

    const renderRowField = (item, row) => {
        const value = item[row.fieldName];

        if (row.renderMethod === RENDER_METHOD_HEADER) {
            return '';
        }

        if (!value || value === N_A) {
            return '-'; 
        }

        switch (row.renderMethod) {
            case RENDER_METHOD_NUMBER:
                return formatMillions(value);
            case RENDER_METHOD_STR:
                return value;
            case RENDER_METHOD_SHARE:
                return formatNumber((parseFloat(value) * 100), 2);
            case RENDER_METHOD_DECIMAL:
                return formatNumber(parseFloat(value), 2);
        }
    }

    return <>
        {incomeStatementFormatted ? <>
            <div className="card z-depth-0 margin-0">
                <div className="card-content padding-top-0-sm padding-right-0-sm padding-bottom-30-sm padding-left-0-sm"> 
                    <h2 className="card-title" data-testid="incomeStatementTableHeader">Income statement</h2>
                    <table className="data-table data-table--medium data-table--scroll margin-top-0 margin-bottom-30-sm">
                        <thead>
                            <tr>
                                <th></th>
                                {incomeStatementFormatted.map((item, index) => {
                                    return <th key={index} className="text-align-right text-align-left-sm" data-testid={`incomeStatementTableHead${index}`}>{
                                        moment(item.yearEnd, "YYYY-MM-DD").format("YYYY")
                                    }
                                    </th>
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {rowsToRender.map((row, index) => {
                                return <tr key={index} className={row.trClassNames}>
                                    <td data-testid={`${row.fieldName}Label`}>{row.title}</td>
                                    {incomeStatementFormatted.map((item, key) => {
                                        return <td key={`year_ends_columns_${key}`} className="text-align-right text-align-left-sm" data-testid={`${row.fieldName}Value${key}`}>
                                            {renderRowField(item, row)}
                                        </td>
                                    })}
                                </tr>
                            })}
                        </tbody>
                    </table>  
                    {currency ? <p data-testid="incomeStatementCurrencyInMillionsText">{currency} in millions except per share data.</p>: null} 
                </div>
            </div>    
        </> : null}
    </>;

}